import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, Lock, FileSearch, CheckCircle2, AlertTriangle,
  ChevronRight, Eye, Stamp, Send, Plus, RefreshCw, Database,
  Key, Activity, FileText, GitCommit, Search, Filter, X,
  Archive, Upload,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

type RecordStatus   = 'DRAFT' | 'SEALED' | 'VERIFIED' | 'SUBMITTED' | 'ARCHIVED';
type RecordType     = 'EVIDENCE_CAPTURE' | 'SYSTEM_SNAPSHOT' | 'INCIDENT_ARTIFACT' |
                      'AUDIT_LOG_EXTRACT' | 'MEMORY_DUMP' | 'NETWORK_CAPTURE' |
                      'DB_SNAPSHOT' | 'CONFIG_BASELINE';

interface ForensicRecord {
  id: string;
  sessionId: string | null;
  recordType: RecordType;
  status: RecordStatus;
  title: string;
  description: string | null;
  storagePath: string;
  fileSize: number;
  mimeType?: string | null;
  sha256: string;
  prevRecordHash: string;
  recordHash: string | null;
  digitalSignature: string | null;
  signingKeyId: string | null;
  sealedAt: string | null;
  sealedBy: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
  retentionUntil: string;
  tags: string[];
  metadata: Record<string, unknown> | null;
  createdBy: string;
  createdAt: string;
}

interface CoCEntry {
  id: string;
  recordId: string;
  action: string;
  actorName: string;
  actorRole: string;
  clientIp: string | null;
  notes: string | null;
  entryHash: string;
  createdAt: string;
}

interface ForensicSeal {
  id: string;
  sealCode: string;
  title: string;
  status: string;
  recordIds: string[];
  manifestHash: string | null;
  sealedAt: string | null;
  submittedTo: string | null;
  createdAt: string;
}

interface HsmStatus {
  healthy: boolean;
  keyId: string;
  fingerprint: string;
  publicKey: string;
}

interface Stats {
  totalRecords: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  totalSeals: number;
  recentActivity: CoCEntry[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<RecordStatus, { label: string; color: string; icon: typeof Lock }> = {
  DRAFT:     { label: 'Bản nháp',   color: 'slate',   icon: FileText },
  SEALED:    { label: 'Đã niêm phong', color: 'indigo', icon: Lock },
  VERIFIED:  { label: 'Đã xác nhận', color: 'emerald', icon: CheckCircle2 },
  SUBMITTED: { label: 'Đã nộp',     color: 'blue',    icon: Send },
  ARCHIVED:  { label: 'Lưu trữ',    color: 'amber',   icon: Archive },
};

const TYPE_LABELS: Record<RecordType, string> = {
  EVIDENCE_CAPTURE:  'Thu thập bằng chứng',
  SYSTEM_SNAPSHOT:   'Snapshot hệ thống',
  INCIDENT_ARTIFACT: 'Artifact sự cố',
  AUDIT_LOG_EXTRACT: 'Trích xuất audit log',
  MEMORY_DUMP:       'Memory dump',
  NETWORK_CAPTURE:   'PCAP mạng',
  DB_SNAPSHOT:       'Snapshot CSDL',
  CONFIG_BASELINE:   'Baseline cấu hình',
};

const ACTION_ICON: Record<string, typeof Lock> = {
  RECORD_CREATED:    Plus,
  FILE_UPLOADED:     Upload,
  HASH_COMPUTED:     Database,
  RECORD_SEALED:     Lock,
  RECORD_VERIFIED:   CheckCircle2,
  RECORD_SUBMITTED:  Send,
  RECORD_ACCESSED:   Eye,
  INTEGRITY_CHECKED: ShieldCheck,
  INTEGRITY_FAILED:  AlertTriangle,
  PACKAGE_EXPORTED:  FileText,
  STATUS_CHANGED:    RefreshCw,
  RECORD_ARCHIVED:   Archive,
};

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

// ─── Create Record Modal ─────────────────────────────────────────────────────

function CreateRecordModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    recordType: 'EVIDENCE_CAPTURE' as RecordType,
    storagePath: '/forensic-store/',
    description: '',
    tags: '',
  });

  const create = useMutation({
    mutationFn: () => api.post('/forensic/records', {
      ...form,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [],
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forensic-records'] });
      qc.invalidateQueries({ queryKey: ['forensic-stats'] });
      toast.success('Đã tạo forensic record');
      onClose();
    },
    onError: () => toast.error('Lỗi tạo record'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-[#161617] border border-slate-200 dark:border-white/10 rounded-3xl w-full max-w-lg shadow-2xl"
      >
        <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
          <h3 className="font-black text-slate-900 dark:text-white flex items-center gap-2">
            <Plus size={18} className="text-indigo-500" /> Tạo Forensic Record
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Tiêu đề *</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="VD: Memory dump server PROD-01 sau sự cố 08/06/2026"
              className="mt-1.5 w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Loại artifact *</label>
            <select
              value={form.recordType}
              onChange={e => setForm(f => ({ ...f, recordType: e.target.value as RecordType }))}
              className="mt-1.5 w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500"
            >
              {Object.entries(TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Đường dẫn lưu trữ *</label>
            <input
              value={form.storagePath}
              onChange={e => setForm(f => ({ ...f, storagePath: e.target.value }))}
              placeholder="/forensic-store/2026/memory_dump_prod01.bin"
              className="mt-1.5 w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-mono text-slate-900 dark:text-white outline-none focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Mô tả</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Mô tả chi tiết về artifact này..."
              className="mt-1.5 w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500 resize-none"
            />
          </div>
          <div>
            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">Tags (cách nhau bởi dấu phẩy)</label>
            <input
              value={form.tags}
              onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="incident-2026-06, prod, memory"
              className="mt-1.5 w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500"
            />
          </div>
        </div>
        <div className="p-6 pt-2 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 text-sm font-black hover:bg-slate-200 dark:hover:bg-white/10 transition-all"
          >
            Hủy
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!form.title || !form.storagePath || create.isPending}
            className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-black hover:bg-indigo-700 disabled:opacity-50 transition-all"
          >
            {create.isPending ? 'Đang tạo...' : 'Tạo Record'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Chain of Custody Panel ───────────────────────────────────────────────────

function ChainOfCustodyPanel({ recordId, onClose }: { recordId: string; onClose: () => void }) {
  const { data: coc = [], isLoading } = useQuery<CoCEntry[]>({
    queryKey: ['forensic-coc', recordId],
    queryFn: () => api.get(`/forensic/records/${recordId}/chain-of-custody`).then(r => r.data),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm p-6">
      <motion.div
        initial={{ x: 400, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 400, opacity: 0 }}
        className="bg-white dark:bg-[#111113] border border-slate-200 dark:border-white/10 rounded-3xl w-full max-w-md h-[85vh] flex flex-col shadow-2xl"
      >
        <div className="p-5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between shrink-0">
          <h3 className="font-black text-slate-900 dark:text-white flex items-center gap-2 text-sm">
            <GitCommit size={16} className="text-indigo-500" />
            Chain of Custody
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-20 text-slate-400 text-sm">Đang tải...</div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-4 bottom-4 w-px bg-slate-200 dark:bg-white/10" />

              <div className="space-y-4">
                {coc.map((entry, i) => {
                  const Icon = ACTION_ICON[entry.action] ?? Activity;
                  const isFailed = entry.action === 'INTEGRITY_FAILED';
                  return (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="flex gap-4 pl-0"
                    >
                      <div className={`relative z-10 w-8 h-8 shrink-0 rounded-full flex items-center justify-center border-2 ${
                        isFailed
                          ? 'bg-rose-100 dark:bg-rose-500/20 border-rose-400'
                          : entry.action === 'RECORD_SEALED'
                          ? 'bg-indigo-100 dark:bg-indigo-500/20 border-indigo-400'
                          : entry.action === 'RECORD_VERIFIED'
                          ? 'bg-emerald-100 dark:bg-emerald-500/20 border-emerald-400'
                          : 'bg-white dark:bg-[#1a1a1c] border-slate-200 dark:border-white/10'
                      }`}>
                        <Icon size={14} className={
                          isFailed ? 'text-rose-500' :
                          entry.action === 'RECORD_SEALED' ? 'text-indigo-500' :
                          entry.action === 'RECORD_VERIFIED' ? 'text-emerald-500' :
                          'text-slate-500 dark:text-slate-400'
                        } />
                      </div>

                      <div className="flex-1 pb-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-xs font-black uppercase tracking-wider ${
                            isFailed ? 'text-rose-500' : 'text-slate-600 dark:text-slate-300'
                          }`}>
                            {entry.action.replace(/_/g, ' ')}
                          </span>
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {new Date(entry.createdAt).toLocaleString('vi-VN')}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          <span className="font-bold">{entry.actorName}</span>
                          {entry.clientIp && <span className="ml-2 font-mono opacity-60">{entry.clientIp}</span>}
                        </div>
                        {entry.notes && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-1 leading-relaxed">
                            {entry.notes}
                          </p>
                        )}
                        <p className="text-[9px] font-mono text-slate-300 dark:text-slate-700 mt-1 break-all">
                          {entry.entryHash.slice(0, 24)}...
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
                {coc.length === 0 && (
                  <p className="text-center text-slate-400 text-sm py-8">Chưa có sự kiện nào</p>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Record Detail Modal ─────────────────────────────────────────────────────

function RecordDetailModal({ record, onClose, onRefresh }: {
  record: ForensicRecord;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const qc = useQueryClient();
  const [showCoc, setShowCoc] = useState(false);

  const seal = useMutation({
    mutationFn: () => api.post(`/forensic/records/${record.id}/seal`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['forensic-records'] }); onRefresh(); toast.success('Đã niêm phong RSA-4096'); },
    onError: () => toast.error('Lỗi niêm phong'),
  });

  const verify = useMutation({
    mutationFn: () => api.post(`/forensic/records/${record.id}/verify`, {}),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['forensic-records'] });
      const d = res.data;
      if (d.integrityOk && d.signatureOk) {
        toast.success('Xác minh thành công — integrity OK, chữ ký hợp lệ');
      } else {
        toast.error(`Xác minh THẤT BẠI: integrity=${d.integrityOk}, sig=${d.signatureOk}`);
      }
      onRefresh();
    },
    onError: () => toast.error('Lỗi xác minh'),
  });

  const cfg = STATUS_CONFIG[record.status];
  const StatusIcon = cfg.icon;

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white dark:bg-[#161617] border border-slate-200 dark:border-white/10 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-start justify-between sticky top-0 bg-white dark:bg-[#161617]">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-${cfg.color}-100 dark:bg-${cfg.color}-500/15 text-${cfg.color}-700 dark:text-${cfg.color}-400`}>
                  <StatusIcon size={10} /> {cfg.label}
                </span>
                <span className="text-[10px] font-black text-slate-400 uppercase">{TYPE_LABELS[record.recordType]}</span>
              </div>
              <h3 className="font-black text-slate-900 dark:text-white text-sm leading-snug max-w-lg">{record.title}</h3>
              <p className="text-[10px] font-mono text-slate-400 mt-1">{record.id}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white ml-4 shrink-0"><X size={20} /></button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Hash chain */}
            <div>
              <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-3">Hash Chain & Integrity</h4>
              <div className="bg-slate-50 dark:bg-black/30 rounded-2xl p-4 space-y-2.5">
                {[
                  { label: 'SHA-256 (file)',    val: record.sha256 },
                  { label: 'Record Hash',       val: record.recordHash    ?? '—' },
                  { label: 'Prev Record Hash',  val: record.prevRecordHash },
                  { label: 'Signing Key ID',    val: record.signingKeyId  ?? '—' },
                ].map(row => (
                  <div key={row.label}>
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider">{row.label}</span>
                    <p className="text-[11px] font-mono text-slate-700 dark:text-slate-300 break-all mt-0.5">{row.val}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Storage info */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Storage Path', val: record.storagePath, mono: true },
                { label: 'File Size',    val: formatBytes(record.fileSize), mono: false },
                { label: 'MIME Type',   val: record.mimeType ?? '—', mono: false },
                { label: 'Retention',   val: new Date(record.retentionUntil).toLocaleDateString('vi-VN'), mono: false },
                { label: 'Created',     val: new Date(record.createdAt).toLocaleString('vi-VN'), mono: false },
                { label: 'Sealed At',   val: record.sealedAt ? new Date(record.sealedAt).toLocaleString('vi-VN') : '—', mono: false },
              ].map(f => (
                <div key={f.label} className="bg-slate-50 dark:bg-white/[0.03] rounded-xl p-3">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{f.label}</span>
                  <p className={`text-xs mt-0.5 text-slate-700 dark:text-slate-300 break-all ${f.mono ? 'font-mono' : 'font-bold'}`}>{f.val}</p>
                </div>
              ))}
            </div>

            {/* Tags */}
            {record.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {record.tags.map(t => (
                  <span key={t} className="px-2 py-0.5 bg-slate-100 dark:bg-white/5 rounded-full text-[10px] font-black text-slate-500">#{t}</span>
                ))}
              </div>
            )}

            {/* Description */}
            {record.description && (
              <div>
                <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Mô tả</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{record.description}</p>
              </div>
            )}

            {/* Digital signature preview */}
            {record.digitalSignature && (
              <div>
                <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                  <Lock size={10} className="text-indigo-500" /> Chữ ký RSA-4096 (HSM)
                </h4>
                <div className="bg-indigo-50 dark:bg-indigo-500/10 rounded-xl p-3 border border-indigo-200 dark:border-indigo-500/20">
                  <p className="text-[10px] font-mono text-indigo-600 dark:text-indigo-400 break-all">
                    {record.digitalSignature.slice(0, 120)}...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 pt-0 flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowCoc(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-300 text-xs font-black hover:bg-slate-200 dark:hover:bg-white/10 transition-all"
            >
              <GitCommit size={14} /> Chain of Custody
            </button>

            {record.status === 'DRAFT' && (
              <button
                onClick={() => seal.mutate()}
                disabled={seal.isPending}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-xs font-black hover:bg-indigo-700 disabled:opacity-50 transition-all"
              >
                <Lock size={14} />
                {seal.isPending ? 'Đang niêm phong...' : 'Niêm phong RSA-4096'}
              </button>
            )}

            {record.status === 'SEALED' && (
              <button
                onClick={() => verify.mutate()}
                disabled={verify.isPending}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700 disabled:opacity-50 transition-all"
              >
                <ShieldCheck size={14} />
                {verify.isPending ? 'Đang xác minh...' : 'Xác minh C12'}
              </button>
            )}
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showCoc && (
          <ChainOfCustodyPanel recordId={record.id} onClose={() => setShowCoc(false)} />
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ForensicStore() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate]   = useState(false);
  const [selectedRecord, setSelected] = useState<ForensicRecord | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab]   = useState<'records' | 'seals' | 'hsm'>('records');

  const { data: recordsRes, isLoading: loadingRecords } = useQuery<{
    data: ForensicRecord[]; total: number;
  }>({
    queryKey: ['forensic-records', filterStatus, search],
    queryFn: () => api.get('/forensic/records', {
      params: { status: filterStatus || undefined, search: search || undefined, limit: 50 },
    }).then(r => r.data),
  });

  const { data: stats } = useQuery<Stats>({
    queryKey: ['forensic-stats'],
    queryFn: () => api.get('/forensic/stats').then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: hsmStatus } = useQuery<HsmStatus>({
    queryKey: ['forensic-hsm'],
    queryFn: () => api.get('/forensic/hsm/status').then(r => r.data),
  });

  const { data: sealsRes } = useQuery<{ data: ForensicSeal[]; total: number }>({
    queryKey: ['forensic-seals'],
    queryFn: () => api.get('/forensic/seals').then(r => r.data),
    enabled: activeTab === 'seals',
  });

  const records = recordsRes?.data ?? [];

  const statCards = [
    { label: 'Tổng records',    value: stats?.totalRecords ?? 0,              color: 'indigo', icon: Database },
    { label: 'Đã niêm phong',  value: stats?.byStatus?.['SEALED'] ?? 0,      color: 'indigo', icon: Lock },
    { label: 'Đã xác nhận',    value: stats?.byStatus?.['VERIFIED'] ?? 0,    color: 'emerald', icon: CheckCircle2 },
    { label: 'Gói niêm phong', value: stats?.totalSeals ?? 0,                color: 'amber',  icon: Stamp },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
            <ShieldCheck size={28} className="text-indigo-500" />
            Forensic <span className="text-indigo-500">Store</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            Hồ sơ điều tra số · HSM RSA-4096 · Chain of Custody · Lưu trữ 7 năm
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
              hsmStatus?.healthy
                ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                : 'bg-rose-100 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${hsmStatus?.healthy ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
              HSM {hsmStatus?.healthy ? 'Online' : 'Offline'}
            </span>
            {hsmStatus && (
              <span className="text-[10px] font-mono text-slate-400">
                {hsmStatus.keyId}
              </span>
            )}
            <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20">
              ⚠ KHÔNG KẾT NỐI NDOP
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-indigo-600 text-white font-black text-sm hover:bg-indigo-700 transition-all"
        >
          <Plus size={16} /> Tạo Record
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-5"
          >
            <div className={`w-10 h-10 rounded-2xl bg-${s.color}-100 dark:bg-${s.color}-500/15 flex items-center justify-center mb-3`}>
              <s.icon size={18} className={`text-${s.color}-600 dark:text-${s.color}-400`} />
            </div>
            <p className="text-2xl font-black text-slate-900 dark:text-white">{s.value}</p>
            <p className="text-xs text-slate-500 font-bold mt-0.5">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-2xl w-fit">
        {(['records', 'seals', 'hsm'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
              activeTab === tab
                ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {tab === 'records' ? `Records (${records.length})` :
             tab === 'seals'   ? `Seal Packages (${sealsRes?.total ?? 0})` :
             'HSM Status'}
          </button>
        ))}
      </div>

      {/* Records tab */}
      {activeTab === 'records' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Tìm kiếm title..."
                className="pl-9 pr-4 py-2.5 w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white outline-none focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-slate-400" />
              {['', 'DRAFT', 'SEALED', 'VERIFIED', 'SUBMITTED', 'ARCHIVED'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${
                    filterStatus === s
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-white/10'
                  }`}
                >
                  {s || 'Tất cả'}
                </button>
              ))}
            </div>
          </div>

          {/* Records list */}
          {loadingRecords ? (
            <div className="text-center py-20 text-slate-400">Đang tải...</div>
          ) : records.length === 0 ? (
            <div className="text-center py-20">
              <FileSearch size={48} className="mx-auto text-slate-300 dark:text-slate-700 mb-4" />
              <p className="text-slate-400 font-medium">Chưa có forensic record nào.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {records.map((rec, i) => {
                const cfg = STATUS_CONFIG[rec.status];
                const Icon = cfg.icon;
                return (
                  <motion.div
                    key={rec.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => setSelected(rec)}
                    className="bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:border-indigo-500/50 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/5 transition-all group"
                  >
                    <div className={`w-10 h-10 shrink-0 rounded-xl bg-${cfg.color}-100 dark:bg-${cfg.color}-500/15 flex items-center justify-center`}>
                      <Icon size={18} className={`text-${cfg.color}-600 dark:text-${cfg.color}-400`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-black text-sm text-slate-900 dark:text-white truncate">{rec.title}</p>
                        {rec.digitalSignature && (
                          <Lock size={12} className="text-indigo-400 shrink-0" aria-label="Đã ký RSA-4096" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-[10px] font-bold text-slate-400">{TYPE_LABELS[rec.recordType]}</span>
                        <span className="text-[10px] font-mono text-slate-300 dark:text-slate-600">{rec.sha256.slice(0, 12)}...</span>
                        {rec.sealedAt && (
                          <span className="text-[10px] text-slate-400">
                            Sealed: {new Date(rec.sealedAt).toLocaleDateString('vi-VN')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {rec.tags.slice(0, 2).map(t => (
                        <span key={t} className="px-2 py-0.5 bg-slate-100 dark:bg-white/5 rounded-full text-[9px] font-black text-slate-500">#{t}</span>
                      ))}
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black bg-${cfg.color}-100 dark:bg-${cfg.color}-500/15 text-${cfg.color}-700 dark:text-${cfg.color}-400`}>
                        {cfg.label}
                      </span>
                      <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-500 transition-colors" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Seals tab */}
      {activeTab === 'seals' && (
        <div className="space-y-3">
          {(sealsRes?.data ?? []).length === 0 ? (
            <div className="text-center py-20">
              <Stamp size={48} className="mx-auto text-slate-300 dark:text-slate-700 mb-4" />
              <p className="text-slate-400 font-medium">Chưa có gói niêm phong nào.</p>
              <p className="text-slate-400 text-sm mt-1">Chọn nhiều record SEALED để tạo gói.</p>
            </div>
          ) : sealsRes?.data.map((seal, i) => (
            <motion.div
              key={seal.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 rounded-2xl p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-black text-indigo-500">{seal.sealCode}</span>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black ${
                      seal.status === 'SUBMITTED' ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400' :
                      seal.status === 'SEALED'    ? 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400' :
                      'bg-slate-100 dark:bg-white/5 text-slate-500'
                    }`}>
                      {seal.status}
                    </span>
                  </div>
                  <p className="font-bold text-slate-900 dark:text-white mt-1">{seal.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {seal.recordIds.length} records · {seal.sealedAt ? new Date(seal.sealedAt).toLocaleDateString('vi-VN') : 'Chưa seal'}
                    {seal.submittedTo && ` → ${seal.submittedTo}`}
                  </p>
                </div>
                <div className="text-right">
                  {seal.manifestHash && (
                    <p className="text-[10px] font-mono text-slate-300 dark:text-slate-700">{seal.manifestHash.slice(0, 16)}...</p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* HSM tab */}
      {activeTab === 'hsm' && (
        <div className="space-y-4">
          {hsmStatus ? (
            <>
              <div className="bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${hsmStatus.healthy ? 'bg-emerald-100 dark:bg-emerald-500/15' : 'bg-rose-100 dark:bg-rose-500/15'}`}>
                    <Key size={22} className={hsmStatus.healthy ? 'text-emerald-500' : 'text-rose-500'} />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-900 dark:text-white">HSM Status: {hsmStatus.healthy ? '● Online' : '○ Offline'}</h3>
                    <p className="text-xs text-slate-500">RSA-4096 · Private key KHÔNG BAO GIỜ rời HSM</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Key ID', val: hsmStatus.keyId, mono: true },
                    { label: 'Fingerprint (SHA-256)', val: hsmStatus.fingerprint, mono: true },
                  ].map(f => (
                    <div key={f.label} className="bg-slate-50 dark:bg-black/30 rounded-xl p-3">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{f.label}</p>
                      <p className="text-xs font-mono text-slate-700 dark:text-slate-300 break-all mt-0.5">{f.val}</p>
                    </div>
                  ))}
                  <div className="bg-slate-50 dark:bg-black/30 rounded-xl p-3">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Public Key (PEM)</p>
                    <pre className="text-[9px] font-mono text-slate-600 dark:text-slate-400 whitespace-pre-wrap break-all leading-relaxed max-h-40 overflow-y-auto">
                      {hsmStatus.publicKey}
                    </pre>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-black text-amber-700 dark:text-amber-400 uppercase tracking-wider">Security Notice</p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1 leading-relaxed">
                      HSM hiện dùng chế độ <strong>SOFTWARE_STUB</strong>. Trong production, thay thế bằng PKCS#11 hardware token (SoftHSM2 / Thales Luna nShield / nCipher).
                      Private key RSA-4096 <strong>KHÔNG BAO GIỜ</strong> xuất hiện trong log, response, hay bất kỳ storage nào.
                      Forensic data <strong>KHÔNG BAO GIỜ</strong> đi qua NDOP gateway.
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent activity */}
              {(stats?.recentActivity ?? []).length > 0 && (
                <div className="bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 rounded-3xl p-6">
                  <h3 className="font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2 text-sm">
                    <Activity size={16} className="text-indigo-500" /> Hoạt động gần đây
                  </h3>
                  <div className="space-y-2">
                    {stats!.recentActivity.map(entry => {
                      const Icon = ACTION_ICON[entry.action] ?? Activity;
                      return (
                        <div key={entry.id} className="flex items-center gap-3 py-2 border-b border-slate-100 dark:border-white/5 last:border-0">
                          <Icon size={14} className="text-slate-400 shrink-0" />
                          <span className="text-xs font-black text-slate-700 dark:text-slate-300 flex-1">{entry.action.replace(/_/g, ' ')}</span>
                          <span className="text-[10px] text-slate-400">{entry.actorName}</span>
                          <span className="text-[10px] font-mono text-slate-300 dark:text-slate-700">
                            {new Date(entry.createdAt).toLocaleTimeString('vi-VN')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20 text-slate-400">Đang kết nối HSM...</div>
          )}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showCreate && <CreateRecordModal onClose={() => setShowCreate(false)} />}
      </AnimatePresence>

      {selectedRecord && (
        <RecordDetailModal
          record={selectedRecord}
          onClose={() => setSelected(null)}
          onRefresh={() => {
            qc.invalidateQueries({ queryKey: ['forensic-records'] });
            qc.invalidateQueries({ queryKey: ['forensic-stats'] });
          }}
        />
      )}
    </div>
  );
}
